import type { DataSyncTaskLogDetail } from './validations/types';

import { type UseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataSyncJob } from './queryKeys';

/**
 * Fetches a single data sync task log by ID with full details.
 * Includes logs and error_logs fields.
 *
 * @param logId - The UUID of the log to fetch
 * @returns Promise containing the log detail data
 */
export const fetchDataSyncLog = async (
  logId: UUID,
): Promise<DataSyncTaskLogDetail> => {
  const response = await vestigasApi
    .get(`data_sync/log/${logId}`)
    .json<DataSyncTaskLogDetail>();

  return response;
};

/**
 * React Query hook for fetching a single data sync task log with full details.
 *
 * @param logId - The UUID of the log to fetch
 * @param options - Additional React Query options
 * @returns Query result containing the log detail data
 */
export const useQueryDataSyncLog = (
  logId: UUID,
  options?: Omit<
    UseQueryOptions<DataSyncTaskLogDetail>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryKey = queryKeysDataSyncJob.getLogById(logId);

  return useSuspenseQuery({
    queryFn: async () => fetchDataSyncLog(logId),
    queryKey,
    staleTime: ms('5m'),
    ...options,
  });
};
